import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Video, ExternalLink, Eye, EyeOff, Calendar, Clock, Play } from 'lucide-react';

export default function VideoManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    youtube_url: '',
    session_date: '',
    session_number: '',
    duration_minutes: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/all`);
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
    setLoading(false);
  };

  const handleAddVideo = async () => {
    if (!newVideo.title || !newVideo.youtube_url) {
      toast.error('Title and YouTube URL are required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newVideo,
          session_number: newVideo.session_number ? parseInt(newVideo.session_number) : null,
          duration_minutes: newVideo.duration_minutes ? parseInt(newVideo.duration_minutes) : null
        })
      });

      if (response.ok) {
        toast.success('Video added successfully!');
        setShowAddDialog(false);
        setNewVideo({
          title: '',
          description: '',
          youtube_url: '',
          session_date: '',
          session_number: '',
          duration_minutes: ''
        });
        fetchVideos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add video');
      }
    } catch (error) {
      toast.error('Error adding video');
    }
  };

  const handleToggleActive = async (videoId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/toggle/${videoId}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.active ? 'Video activated' : 'Video deactivated');
        fetchVideos();
      }
    } catch (error) {
      toast.error('Error toggling video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/${videoId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Video deleted');
        fetchVideos();
      }
    } catch (error) {
      toast.error('Error deleting video');
    }
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const previewId = newVideo.youtube_url ? extractYouTubeId(newVideo.youtube_url) : null;

  return (
    <Card className="bg-stone-950 border-stone-800">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-amber-500 flex items-center gap-2">
          <Video className="w-5 h-5" />
          Session Video Recordings
        </CardTitle>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-500 text-black">
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-stone-900 border-stone-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Add Session Recording</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-stone-300">Title *</Label>
                <Input
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  placeholder="Session 1: Introduction to NVC"
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-stone-300">YouTube URL *</Label>
                <Input
                  value={newVideo.youtube_url}
                  onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-stone-800 border-stone-700 text-white"
                />
                {previewId && (
                  <div className="mt-2 rounded overflow-hidden aspect-video max-w-sm">
                    <img 
                      src={`https://img.youtube.com/vi/${previewId}/mqdefault.jpg`}
                      alt="Video preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-stone-300">Description</Label>
                <Textarea
                  value={newVideo.description}
                  onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                  placeholder="Brief description of what's covered in this session..."
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-stone-300">Session Number</Label>
                  <Input
                    type="number"
                    value={newVideo.session_number}
                    onChange={(e) => setNewVideo({ ...newVideo, session_number: e.target.value })}
                    placeholder="1"
                    className="bg-stone-800 border-stone-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">Session Date</Label>
                  <Input
                    type="date"
                    value={newVideo.session_date}
                    onChange={(e) => setNewVideo({ ...newVideo, session_date: e.target.value })}
                    className="bg-stone-800 border-stone-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">Duration (min)</Label>
                  <Input
                    type="number"
                    value={newVideo.duration_minutes}
                    onChange={(e) => setNewVideo({ ...newVideo, duration_minutes: e.target.value })}
                    placeholder="60"
                    className="bg-stone-800 border-stone-700 text-white"
                  />
                </div>
              </div>

              <Button onClick={handleAddVideo} className="w-full bg-amber-600 hover:bg-amber-500 text-black">
                Add Video Recording
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {loading ? (
          <p className="text-stone-500 text-center py-8">Loading videos...</p>
        ) : videos.length === 0 ? (
          <p className="text-stone-500 text-center py-8">No video recordings yet. Add your first session recording!</p>
        ) : (
          <div className="space-y-4">
            {videos.map(video => (
              <div 
                key={video._id}
                className={`flex items-start gap-4 p-4 rounded-lg border ${video.active ? 'bg-stone-900/50 border-stone-700' : 'bg-stone-900/20 border-stone-800 opacity-60'}`}
              >
                {/* Thumbnail */}
                <div className="w-40 h-24 rounded overflow-hidden flex-shrink-0 relative group">
                  <img 
                    src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <a 
                    href={video.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Play className="w-8 h-8 text-white" />
                  </a>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium truncate">{video.title}</h4>
                  {video.description && (
                    <p className="text-stone-400 text-sm mt-1 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-stone-500 text-xs">
                    {video.session_number && (
                      <span className="flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        Session {video.session_number}
                      </span>
                    )}
                    {video.session_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(video.session_date).toLocaleDateString()}
                      </span>
                    )}
                    {video.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {video.duration_minutes} min
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(video._id)}
                    className={video.active ? 'border-emerald-600 text-emerald-400' : 'border-stone-600 text-stone-400'}
                  >
                    {video.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <a 
                    href={video.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="border-stone-600 text-stone-400">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteVideo(video._id)}
                    className="border-red-600 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
